import { useEffect, useState } from "react";
import "./birthplace-picker-style.css";
import GeneralService from "../../../service/general-service";

//出生地級聯選擇 dialog（用 G-1 birthplace-cascade 逐層下鑽）
//  Props:
//    open       - 是否顯示
//    onClose    - 關閉 callback
//    onSelect   - 選定時的 callback，回傳 { fullName, lat, lng }
const BirthplacePicker = ({ open, onClose, onSelect }) => {
  //已選路徑（每項 { value, label }）；長度 0 = 在國家層
  const [path, setPath] = useState([]);
  //當前層可選項目
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  //拉某一路徑的清單
  //  path 傳完整路徑陣列（[{ value }, { value }, ...]）
  //  若後端回 type "1"（終點）→ 用 onSelect 回傳，關閉 dialog
  //  否則設定 items
  const fetchLevel = async (nextPath) => {
    setLoading(true);
    try {
      const [level1, level2, level3] = nextPath.map((p) => p.value);
      const res = await GeneralService.birthplaceCascade({
        level1,
        level2,
        level3,
      });
      const data = res.data?.data;
      if (!data) {
        setItems([]);
        return;
      }
      if (data.type === "1") {
        //終點：直接 onSelect + 關閉
        //  後端 fullName 是物件 { en, zhHant, zhHans }，這裡抽 zhHant 給前端用（純字串）
        const fn = data.result?.fullName;
        const fullName =
          (typeof fn === "string" && fn) || fn?.zhHant || fn?.en || "";
        onSelect?.({
          fullName,
          lat: data.result?.lat,
          lng: data.result?.lng,
        });
        onClose?.();
        return;
      }
      //清單
      setItems(data.items || []);
      setPath(nextPath);
    } catch (e) {
      console.error("[BirthplacePicker] 拉層失敗:", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  //打開時：reset path + 拉國家清單
  useEffect(() => {
    if (!open) return;
    setPath([]);
    fetchLevel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  //鎖背景滾動 + ESC 關閉
  useEffect(() => {
    if (!open) return;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  //點項目 → 若 hasNext 下鑽、若不是終點會被 API 端判定
  const handleItemClick = (item) => {
    const nextPath = [
      ...path,
      { value: item.value, label: item.name?.zhHant || item.value },
    ];
    fetchLevel(nextPath);
  };

  //返回上一層
  const handleBack = () => {
    const nextPath = path.slice(0, -1);
    fetchLevel(nextPath);
  };

  if (!open) return null;

  return (
    <div className="birthplace-backdrop" onMouseDown={onClose}>
      <div
        className="birthplace-dialog"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="close-btn" onClick={onClose}>
          <img src="/images/ic-white-close.png" alt="close" />
        </div>

        <div className="birthplace-header">
          <p className="title">選擇出生地</p>
          {path.length > 0 && (
            <div className="breadcrumb">
              {path.map((p, idx) => (
                <span key={idx} className="crumb">
                  {p.label}
                  {idx < path.length - 1 && (
                    <span className="sep">&gt;</span>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="birthplace-body">
          {loading ? (
            <div className="loading">載入中…</div>
          ) : items.length === 0 ? (
            <div className="empty">目前沒有可選項目</div>
          ) : (
            items.map((item) => (
              <div
                className="item"
                key={item.value}
                onClick={() => handleItemClick(item)}
              >
                <span className="name">
                  {item.name?.zhHant || item.value}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="birthplace-footer">
          {path.length > 0 && (
            <button className="back-btn" onClick={handleBack}>
              返回上一層
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BirthplacePicker;
